import { Injectable } from '@angular/core';
import { CanonicalWorkOrderState, PORTAL_STATE_BY_CANONICAL_STATE, WorkOrderState } from 'app/_models/work-order-state';

@Injectable()
export class WorkOrderStateService {
  public setWorkOrderState(workOrder: { approvalStatus: string; released: any; status: string; scheduledStartTime: string; tocStatus: string; workOrderState?: CanonicalWorkOrderState }) {
    // The backend's state wins. The calculation below is the fallback for responses that
    // don't carry it, such as the dashboard's work orders and the lite DTOs.
    const canonicalState = PORTAL_STATE_BY_CANONICAL_STATE[workOrder.workOrderState];
    if (canonicalState) {
      return canonicalState;
    }

    let workOrderState: WorkOrderState;

    if (workOrder.approvalStatus === 'PENDING') {
      workOrderState = WorkOrderState.Pending;

    } else if (workOrder.approvalStatus === 'REJECTED') {
      workOrderState = WorkOrderState.Rejected;

    } else if (workOrder.approvalStatus === 'APPROVED') {
      workOrderState = WorkOrderState.Approved;

      if (workOrder.status === 'CLOSED') {
        workOrderState = WorkOrderState.Closed;
      } else if (workOrder.released) {
        if (workOrder.status === 'AVAILABLE' && workOrder.scheduledStartTime) {
          let now = new Date();
          let overdueTime = new Date(workOrder.scheduledStartTime);
          // available until 1 hour after scheduled time
          overdueTime.setHours(overdueTime.getHours() + 1);
          if (now.getTime() < overdueTime.getTime()) {
            workOrderState = WorkOrderState.Scheduled;
          } else {
            workOrderState = WorkOrderState.Overdue;
          }
        } else if (workOrder.tocStatus === 'PENDING_XFER') {
          workOrderState = WorkOrderState.PendingXfer;
        } else if (workOrder.tocStatus === 'PENDING_RETURN') {
          workOrderState = WorkOrderState.PendingReturn;
        } else if (workOrder.status === 'SURRENDERED') {
          workOrderState = WorkOrderState.Surrendered;
        } else if (workOrder.tocStatus === 'XFER' && workOrder.status !== 'OPEN') {
          workOrderState = WorkOrderState.XferAcknowledged;
        } else if (workOrder.tocStatus === 'RETURNED' && workOrder.status === 'OPEN') {
          workOrderState = WorkOrderState.ReturnAcknowledged;
        } else if (workOrder.status === 'OPEN') {
          workOrderState = WorkOrderState.Open;
        } else if (workOrder.status === 'SUSPENDED') {
          workOrderState = WorkOrderState.Suspended;
        } else if (workOrder.status === 'AVAILABLE') {
          workOrderState = WorkOrderState.Available;
        }
      }
    }
    return workOrderState;
  }

  public workOrderStateToBadgeClass(workOrderState: WorkOrderState) {
    let colorClass: string;

    switch (workOrderState) {
      case WorkOrderState.Open:
        colorClass = 'success';
        break;
      case WorkOrderState.Available:
      case WorkOrderState.Scheduled:
        colorClass = 'primary';
        break;
      case WorkOrderState.Closed:
        colorClass = 'secondary';
        break;
      case WorkOrderState.Surrendered:
      case WorkOrderState.Rejected:
      case WorkOrderState.PendingXfer:
      case WorkOrderState.PendingReturn:
        colorClass = 'danger';
        break;
      case WorkOrderState.XferAcknowledged:
      case WorkOrderState.ReturnAcknowledged:
      case WorkOrderState.Pending:
      case WorkOrderState.Approved:
      case WorkOrderState.Suspended:
      case WorkOrderState.Overdue:
      default:
        colorClass = 'warning';
        break;
    }

    return colorClass;
  }

  public allowChangeApprovalStatus(workOrderState: WorkOrderState) {
    const permittedStates = [
      WorkOrderState.Pending,
      WorkOrderState.Approved,
      WorkOrderState.Rejected,
      WorkOrderState.Available,
      WorkOrderState.Scheduled,
      WorkOrderState.Overdue,
    ];

    return permittedStates.includes(workOrderState);
  }

  public allowCloseWorkOrderNoWarning(workOrderState: WorkOrderState) {
    const permittedStates = [
      WorkOrderState.Approved,
      WorkOrderState.Available,
      WorkOrderState.Scheduled,
      WorkOrderState.Overdue,
      WorkOrderState.Suspended,
    ];

    return permittedStates.includes(workOrderState);
  }

  public allowsCompetencyEditingInState(workOrderState: WorkOrderState) {
    const permittedStates = [
      WorkOrderState.Pending,
      WorkOrderState.Approved,
      WorkOrderState.Available,
      WorkOrderState.Scheduled,
      WorkOrderState.Overdue,
      WorkOrderState.Suspended,
      WorkOrderState.Surrendered,
    ];

    return permittedStates.includes(workOrderState);
  }

  public allowCloseWorkOrderButWithWarning(workOrderState: WorkOrderState) {
    const permittedStates = [
      WorkOrderState.Open,
    ];

    return permittedStates.includes(workOrderState);
  }

  public allowDeleteWorkOrder(workOrderState: WorkOrderState) {
    const permittedStates = [
      WorkOrderState.Pending,
      WorkOrderState.Approved,
      WorkOrderState.Rejected,
      WorkOrderState.Available,
      WorkOrderState.Scheduled,
      WorkOrderState.Overdue,
      WorkOrderState.Suspended,
      WorkOrderState.Closed,
    ];

    return permittedStates.includes(workOrderState);
  }

  public canHaveSessions(workOrderState: WorkOrderState) {
    const permittedStates = [
      WorkOrderState.Open,
      WorkOrderState.Closed,
      WorkOrderState.Suspended,
      WorkOrderState.Surrendered,
      WorkOrderState.XferAcknowledged,
      WorkOrderState.PendingXfer,
      WorkOrderState.PendingReturn,
      WorkOrderState.ReturnAcknowledged,
    ];

    return permittedStates.includes(workOrderState);
  }

  public groupByState(workOrders: { state: WorkOrderState }[]) {
    return workOrders.reduce((groupedWorkOrders, workOrder) => {
      const siteId = workOrder.state;

      if (!groupedWorkOrders[siteId]) {
        groupedWorkOrders[siteId] = [];
      }
      groupedWorkOrders[siteId].push(workOrder);
      return groupedWorkOrders;
    }, {});
  }

  public compareByDisplayOrder(a, b) {
    const order = [
      WorkOrderState.Pending,
      WorkOrderState.PendingXfer,
      WorkOrderState.PendingReturn,
      WorkOrderState.Approved,
      WorkOrderState.Open,
      WorkOrderState.XferAcknowledged,
      WorkOrderState.Available,
      WorkOrderState.Scheduled,
      WorkOrderState.Overdue,
      WorkOrderState.ReturnAcknowledged,
      WorkOrderState.Suspended,
      WorkOrderState.Surrendered,
    ];

    if (order.indexOf(a) > order.indexOf(b)) {
      return 1;
    } else {
      return -1;
    }
  }
}
